import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input } from '@angular/core';
import { PopoverController } from '@ionic/angular/standalone';
import { RowAction } from './row-actions';

/**
 * The "⋯" menu's contents: a short list in a popover; picking one dismisses with that action.
 * A real menu for the keyboard: it opens on its first item, and the arrow keys, Home and End move
 * between items (Ionic's popover only does that for ion-items).
 */
@Component({
  selector: 'admin-row-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ul role="menu" (keydown)="onKey($event)">
      @for (a of actions; track a.label; let i = $index) {
        @if (a.danger && (i === 0 || !actions[i - 1].danger)) {
          <li class="divider" role="separator"></li>
        }
        <li role="none">
          <button type="button" role="menuitem" [class.danger]="a.danger" (click)="pick(a)">
            @if (a.icon) {
              <span class="material-symbols-outlined" aria-hidden="true">{{ a.icon }}</span>
            }
            {{ a.label }}
          </button>
        </li>
      }
    </ul>
  `,
  styles: [
    `
      ul {
        margin: 0;
        padding: 6px;
        list-style: none;
      }
      button {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 9px 10px;
        border: 0;
        border-radius: 0.6rem;
        background: transparent;
        color: var(--cnt-ink, #222);
        font: inherit;
        font-size: 0.875rem;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
      }
      button:hover,
      button:focus-visible {
        background: var(--cnt-cream, #f7f5ec);
        outline: none;
      }
      button .material-symbols-outlined {
        font-size: 18px;
        color: var(--cnt-muted, #707070);
      }
      button.danger,
      button.danger .material-symbols-outlined {
        color: var(--cnt-danger, #b3261e);
      }
      .divider {
        height: 1px;
        margin: 4px 6px;
        background: var(--cnt-line, rgba(34, 34, 34, 0.08));
      }
    `,
  ],
})
export class AdminRowMenuComponent implements AfterViewInit {
  private readonly popover = inject(PopoverController);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input() actions: RowAction[] = [];

  ngAfterViewInit(): void {
    // The popover focuses itself once it has opened: move to the first item after that.
    const popover = this.host.nativeElement.closest('ion-popover');
    const focusFirst = () => this.items()[0]?.focus();
    if (popover) popover.addEventListener('ionPopoverDidPresent', focusFirst, { once: true });
    else setTimeout(focusFirst);
  }

  onKey(event: KeyboardEvent): void {
    const items = this.items();
    if (!items.length) return;
    const at = items.indexOf(event.target as HTMLButtonElement);
    const next =
      event.key === 'ArrowDown'
        ? (at + 1) % items.length
        : event.key === 'ArrowUp'
          ? (at - 1 + items.length) % items.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : -1;
    if (next < 0) return;
    event.preventDefault();
    items[next].focus();
  }

  private items(): HTMLButtonElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
  }

  pick(action: RowAction): void {
    void this.popover.dismiss(action, 'select');
  }
}
